import getYear from "date-fns/getYear";
import format from "date-fns/format";
import NavbarLayout from "components/layouts/NavbarLayout";
import PageHeading from "components/page/PageHeading";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import Link from "next/link";
import { IAgendaEventType, IIssuerType } from "types/graphqlTypes";
import { CalendarIcon } from "lucide-react";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import useEventsCalendarState from "components/investorrelations/hooks/useEventsCalendarState";
import RightSideFilters, { RightSideFiltersEnum } from "components/investorrelations/RightSideFilters";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import EventsCalendar from "components/investorrelations/EventsCalendar";
import EventsCalendarSearchBar from "components/investorrelations/EventsCalendarSearchBar";
import EventsCalendarList from "components/investorrelations/EventsCalendarList";

function translateIssuerTypeToName(issuerType: IIssuerType) {
  switch (issuerType) {
    case IIssuerType.BrazilianCompany:
      return "Ações";
    case IIssuerType.AmericanCompany:
      return "Stocks";
    case IIssuerType.AmericanReit:
      return "REITs";
    default:
      return "Ações";
  }
}

function EventsCalendarPage() {
  const intl = useIntl();
  const filterState = useEventsCalendarState();

  const pageTitle = getPageTitle(intl, filterState.issuerType);
  const seoDescription = getPageDescription(intl, filterState.issuerType);

  return (
    <NavbarLayout>
      <FundamenteiMetadata title={pageTitle} description={seoDescription} />

      <div className="px-4 pb-8 font-aeonik font-normal xl:mx-auto xl:w-full xl:max-w-7xl xl:px-0">
        <PageHeading
          title={intl.formatMessage(
            {
              defaultMessage: "Agenda de Balanços • {issuerType}",
              id: "P40z6k",
            },
            {
              issuerType: translateIssuerTypeToName(filterState.issuerType),
            },
          )}
          description={intl.formatMessage(
            {
              defaultMessage: "Datas dos últimos balanços de {issuerType} e todos que já foram atualizados!",
              id: "QKHYEG",
            },
            {
              issuerType: translateIssuerTypeToName(filterState.issuerType),
            },
          )}
          icon={<CalendarIcon className="h-6 w-6 fill-transparent" />}
          iconContainerClassName="border-blue-200 bg-blue-50 text-blue-400 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
        />

        <div className="mb-4">
          <Filters />
        </div>

        <EventsCalendar
          {...filterState}
          filterByEventTypes={[
            IAgendaEventType.FirstQuarterEarningsResults,
            IAgendaEventType.SecondQuarterEarningsResults,
            IAgendaEventType.ThirdQuarterEarningsResults,
            IAgendaEventType.FourthQuarterEarningsResults,
          ]}
        >
          {({ data, error, loading, isLoadingMore, loadMoreEvents }) => {
            return (
              <>
                <div className="col-span-12 row-start-2 space-y-4 lg:col-span-8 lg:row-start-1">
                  <EventsCalendarSearchBar {...filterState} />

                  <EventsCalendarList
                    {...filterState}
                    eventsCalendar={data}
                    loading={loading}
                    isLoadingMore={isLoadingMore}
                    loadMoreEvents={loadMoreEvents}
                    error={error}
                  />
                </div>

                <div className="col-span-12 space-y-4 lg:col-span-4">
                  <RightSideFilters
                    {...filterState}
                    eventsCalendar={data}
                    enableFilters={[
                      RightSideFiltersEnum.MyPositions,
                      RightSideFiltersEnum.IsInBookmarks,
                      RightSideFiltersEnum.isInSP500,
                      RightSideFiltersEnum.FilterRange,
                      RightSideFiltersEnum.MonthViewCalendar,
                    ]}
                  />
                </div>
              </>
            );
          }}
        </EventsCalendar>
      </div>
    </NavbarLayout>
  );
}

export default EventsCalendarPage;

function Filters() {
  const intl = useIntl();
  const { issuerType, period, myPositions, isInBookmarks, calendarInterval } = useEventsCalendarState();

  const filterByStartDate = format(calendarInterval.startDate, "yyyy-MM-dd");
  const filterByEndDate = format(calendarInterval.endDate, "yyyy-MM-dd");

  return (
    <div className="space-x-4">
      {[
        intl.locale === "pt-BR"
          ? {
              filterBy: IIssuerType.BrazilianCompany,
              title: "Ações",
              accessibilityDescription: "Filtrar apenas por eventos das Ações",
            }
          : null,
        {
          filterBy: IIssuerType.AmericanCompany,
          title: "Stocks",
          accessibilityDescription: "Filtrar apenas por eventos das Stocks",
        },
        {
          filterBy: IIssuerType.AmericanReit,
          title: "REITs",
          accessibilityDescription: "Filtrar apenas por eventos dos REITs",
        },
      ]
        .filter(notUndefinedOrNull)
        .map((node) => {
          const isActive = issuerType === node.filterBy;
          let linkHref = `/events-calendar?issuerType=${node.filterBy.toLocaleLowerCase()}&startDate=${filterByStartDate}&endDate=${filterByEndDate}&period=${period.toLowerCase()}`;

          if (myPositions) {
            linkHref = `${linkHref}&myPositions=1`;
          }

          if (isInBookmarks) {
            linkHref = `${linkHref}&isInBookmarks=1`;
          }

          return (
            <Link
              key={node.filterBy}
              href={linkHref}
              passHref={true}
              aria-label={node.accessibilityDescription}
              className={classNames(
                "font-aeonik text-sm font-medium",
                "border border-gray-200",
                "rounded-full bg-gray-100 px-4 py-2",
                "text-gray-500",
                "dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
                {
                  "hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700 dark:hover:border-blue-500 dark:hover:bg-blue-900/30 dark:hover:text-blue-400":
                    !isActive,
                  "border-transparent bg-blue-600 text-white dark:bg-blue-600 dark:text-white": isActive,
                },
              )}
            >
              {node.title}
            </Link>
          );
        })}
    </div>
  );
}

function getPageTitle(intl: IntlShape, issuerType: IIssuerType | null) {
  const year = getYear(new Date());
  if (issuerType) {
    if (issuerType === IIssuerType.BrazilianCompany) {
      return intl.formatMessage(
        {
          defaultMessage: "Agenda de {year} para Ações | Fundamentei",
          id: "NQAnYn",
        },
        {
          year,
        },
      );
    }
    if (issuerType === IIssuerType.AmericanCompany) {
      return intl.formatMessage(
        {
          defaultMessage: "Agenda de {year} para Stocks | Fundamentei",
          id: "tbUMKe",
        },
        {
          year,
        },
      );
    }
    if (issuerType === IIssuerType.AmericanReit) {
      return intl.formatMessage(
        {
          defaultMessage: "Agenda de {year} para REITs | Fundamentei",
          id: "UZQjPo",
        },
        {
          year,
        },
      );
    }
  }
  return `Agenda de ${year} | Fundamentei`;
}

function getPageDescription(intl: IntlShape, issuerType: IIssuerType | null) {
  if (issuerType) {
    if (issuerType === IIssuerType.BrazilianCompany) {
      return intl.formatMessage({
        defaultMessage: "Agenda completa de balanços e eventos das empresas listadas na B3",
        id: "ncPxVP",
      });
    }
    if (issuerType === IIssuerType.AmericanCompany) {
      return intl.formatMessage({
        defaultMessage: "Agenda completa de balanços e eventos das Stocks listadas na NYSE e Nasdaq",
        id: "gMK6KZ",
      });
    }
    if (issuerType === IIssuerType.AmericanReit) {
      return intl.formatMessage({
        defaultMessage: "Agenda completa de balanços e eventos dos REITs listados na NYSE e Nasdaq",
        id: "hYcPAg",
      });
    }
  }
  return intl.formatMessage({
    defaultMessage: "Agenda completa de balanços e eventos importantes das empresas listadas na B3, Nasdaq e NYSE.",
    id: "/gDs42",
  });
}
